import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  X, 
  ArrowLeft,
  Filter,
  ChevronDown,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category mapping for team display
const CATEGORY_ORDER = ['General Partners', 'Venture Partners', 'Team', 'Mentors and Advisors'];

// team_type value -> display label for pill badge (Team - <name>)
const TEAM_TYPE_LABELS = {
  general_partners: 'General Partners',
  venture_partners: 'Venture Partners',
  investment_sourcing: 'Team - Investment Sourcing',
  investment_management: 'Team - Investment Management',
  research_and_strategy: 'Team - Research & Strategy',
  investor_relations: 'Team - Investor Relations',
  finance: 'Team - Finance',
  esg: 'Team - ESG',
  investment_growth: 'Team - Investment Growth',
  human_resources: 'Team - People & Talent',
  efficiency_and_effectiveness: 'Team - Efficiency & Effectiveness',
  brand_management: 'Team - Brand Management',
  founders_office: "Team - MD's Office",
  advisors: 'Mentors and Advisors'
};

const CATEGORIES_WITHOUT_PILL = ['General Partners', 'Venture Partners', 'Mentors and Advisors'];

const TeamMemberCard = ({ member, index, category }) => {
  const fullLabel = TEAM_TYPE_LABELS[member.team_type] || (member.team_type ? `Team - ${member.team_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}` : null);
  const teamLabel = fullLabel?.replace(/^Team - /, '') ?? fullLabel;
  const showPill = category && !CATEGORIES_WITHOUT_PILL.includes(category) && teamLabel;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link
        to={`/team/${member.slug || member.member_id}`}
        state={{ fromTeam: true }}
        className="group block"
        data-testid={`team-card-${member.member_id}`}
      >
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-xl transition-all duration-300">
          {/* Photo */}
          <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
            <img
              src={member.photo_url?.startsWith('/') ? `${BACKEND_URL}${member.photo_url}` : member.photo_url || `https://ui-avatars.com/api/?name=${member.name}&background=C8473A&color=fff&size=400`}
              alt={member.photo_alt || member.name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${member.name}&background=C8473A&color=fff&size=400`;
              }}
            />
          </div>

          {/* Content */}
          <div className="p-5 flex flex-col">
            <h3 className="heading-subsection group-hover:text-[#C8473A] transition-colors mb-1">
              {member.name}
            </h3>
            <p className="text-sm text-gray-500 mb-3">{member.designation}</p>
            
            {/* Capabilities */}
            {member.capabilities?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {member.capabilities.slice(0, 3).map((cap, idx) => (
                  <span 
                    key={idx} 
                    className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full"
                  >
                    {cap}
                  </span>
                ))}
                {member.capabilities.length > 3 && (
                  <span className="text-xs text-muted-light">+{member.capabilities.length - 3}</span>
                )}
              </div>
            )}

            {/* Pill badge: only for Team section (not General Partners, Venture Partners, Mentors and Advisors) */}
            {showPill && (
              <div className="mt-auto pt-1.5">
                <Badge variant="secondary" className="bg-[#C8473A]/10 text-yn-brand-on-tint w-fit !py-px leading-none">
                  {teamLabel}
                </Badge>
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const TeamSection = ({ teamType, label, members }) => {
  if (!members?.length) return null;

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mb-16"
    >
      <div className="mb-8">
        <h2 className="heading-section">{label}</h2>
      </div>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {members.map((member, index) => (
          <TeamMemberCard key={member.member_id} member={member} index={index} category={label} />
        ))}
      </div>
    </motion.section>
  );
};

const TeamPage = () => {
  usePageTitle('Team');
  const [groupedTeam, setGroupedTeam] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCapability, setSelectedCapability] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedDesignation, setSelectedDesignation] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupedRes, filtersRes] = await Promise.all([
        axios.get(`${API}/team/grouped`),
        axios.get(`${API}/team/filters`)
      ]);
      setGroupedTeam(groupedRes.data);
      
      // Flatten for search/filter and add category to each member
      const all = groupedRes.data.flatMap(g => 
        g.members.map(member => ({
          ...member,
          category: g.category
        }))
      );
      setAllMembers(all);
      setFilters(filtersRes.data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter logic
  const getFilteredGroupedTeam = () => {
    if (!searchTerm && selectedCapability === 'all' && selectedTeam === 'all' && selectedDesignation === 'all') {
      return groupedTeam;
    }

    const filtered = allMembers.filter(member => {
      const matchesSearch = !searchTerm ||
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.capabilities?.some(c => c.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCapability = selectedCapability === 'all' ||
        member.capabilities?.includes(selectedCapability);

      const matchesTeam = selectedTeam === 'all' || member.category === selectedTeam;

      const matchesDesignation = selectedDesignation === 'all' ||
        (member.designation && member.designation.trim() === selectedDesignation);

      return matchesSearch && matchesCapability && matchesTeam && matchesDesignation;
    });

    // Regroup by category
    const regrouped = {};
    filtered.forEach(member => {
      const category = member.category || 'Team';
      if (!regrouped[category]) regrouped[category] = [];
      regrouped[category].push(member);
    });

    // Sort by display_order first, then alphabetically within each group
    Object.keys(regrouped).forEach(category => {
      regrouped[category].sort((a, b) => {
        // First compare by display_order (lower comes first)
        const orderA = a.display_order ?? 999;
        const orderB = b.display_order ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        // If same display_order, sort alphabetically
        return a.name.localeCompare(b.name);
      });
    });

    // Return in order
    return CATEGORY_ORDER
      .filter(category => regrouped[category]?.length > 0)
      .map(category => ({
        category: category,
        members: regrouped[category]
      }));
  };

  const filteredGroupedTeam = getFilteredGroupedTeam();

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCapability('all');
    setSelectedTeam('all');
    setSelectedDesignation('all');
  };

  const hasActiveFilters = searchTerm || selectedCapability !== 'all' || selectedTeam !== 'all' || selectedDesignation !== 'all';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#211F1F] mb-6">
              The <span className="text-[#C8473A]">YourNesters</span>
            </h1>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-8">
                The YourNest team, committed to infusing Nurture Capital across its portfolio, comprises General & Venture Partners, full-time employees, and a cohort of advisors and mentors.
              </p>
              <p className="text-gray-700 leading-relaxed mb-8">
                Our four General Partners, based in Gurugram and Bengaluru, bring a unique blend of skillsets including M&A, Financial Governance & Restructuring, Executive Development, Deal Flow Management, Technology & Product Engineering, Building Intellectual Property and Global Scale. Another three Venture Partners add Product Development & Technology, HR and Brand Marketing.
              </p>
              <p className="text-gray-700 leading-relaxed mb-8">
                They are supported by a young, passionate team of qualified professionals who manage the business of identifying and investing in potential winners, dealing with investors, founders and peers in the eco-system, and driving exits.
              </p>
              <p className="text-gray-700 leading-relaxed mb-0">
                Founders in our startup portfolio also have access to thought leaders, visionaries and industry experts who constitute a unique network of advisors and mentors from diverse domains.
              </p>
            </div>
          </motion.div>

          {/* Search & Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-8"
          >
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <label htmlFor="team-search" className="sr-only">
                  Search team members
                </label>
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-light pointer-events-none" aria-hidden />
                <Input
                  id="team-search"
                  placeholder="Search by name, role, or capability..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-11 text-base rounded-xl border-gray-200"
                  data-testid="team-search"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={`h-11 px-5 rounded-xl gap-2 ${showFilters ? 'bg-gray-50' : ''}`}
                data-testid="toggle-filters"
                aria-expanded={showFilters}
                aria-controls="team-filters"
                aria-label={hasActiveFilters ? 'Show or hide team filters (filters active)' : 'Show or hide team filters'}
              >
                <Filter className="w-4 h-4" aria-hidden="true" />
                Filters
                {hasActiveFilters && (
                  <span className="w-2 h-2 rounded-full bg-[#C8473A]" aria-hidden="true"></span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} aria-hidden="true" />
              </Button>
            </div>

            {/* Filter Options */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  id="team-filters"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                  aria-label="Team filters"
                >
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100 mt-4">
                    <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                      <SelectTrigger data-testid="filter-team" className="h-10 w-[200px]">
                        <SelectValue placeholder="Team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Teams</SelectItem>
                        {filters?.teams?.map(team => (
                          <SelectItem key={team} value={team}>{team}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedDesignation} onValueChange={setSelectedDesignation}>
                      <SelectTrigger data-testid="filter-designation" className="h-10 w-[200px]">
                        <SelectValue placeholder="Designation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Designations</SelectItem>
                        {filters?.designations?.map(des => (
                          <SelectItem key={des} value={des}>{des}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedCapability} onValueChange={setSelectedCapability}>
                      <SelectTrigger data-testid="filter-capability" className="h-10 w-[200px]">
                        <SelectValue placeholder="Capability" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Capabilities</SelectItem>
                        {filters?.capabilities?.map(cap => (
                          <SelectItem key={cap} value={cap}>{cap}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="text-gray-500 hover:text-[#C8473A] h-10"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Team Sections */}
          {loading ? (
            <div className="space-y-16">
              {[1, 2].map(section => (
                <div key={section}>
                  <div className="h-8 bg-gray-200 rounded w-48 mb-8 animate-pulse"></div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                        <div className="aspect-[4/5] bg-gray-200"></div>
                        <div className="p-5">
                          <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-4 bg-gray-100 rounded w-1/2 mb-3"></div>
                          <div className="flex gap-2">
                            <div className="h-5 bg-gray-100 rounded-full w-16"></div>
                            <div className="h-5 bg-gray-100 rounded-full w-20"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : filteredGroupedTeam.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <Users className="w-14 h-14 mx-auto text-gray-300 mb-4" />
              <h2 className="heading-empty">No team members found</h2>
              <p className="text-gray-500 mb-6">Try adjusting your search or filters</p>
              <Button variant="outline" onClick={clearFilters}>
                Clear all filters
              </Button>
            </motion.div>
          ) : (
            <div data-testid="team-sections">
              {filteredGroupedTeam.map((group) => (
                <TeamSection
                  key={group.category}
                  teamType={group.category}
                  label={group.category}
                  members={group.members}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TeamPage;
